<script lang="ts" setup>
    import '../../../css/choice.css';

    import * as ChoiceApi from '../../lib/ChoiceApi';
    import { onMounted, ref, reactive, computed, nextTick } from 'vue';
    import { ButtonDisplaySize, ButtonDisplayStyle, DisplayStyle } from '../../Enums/UiEnums';
    import { ICol, RenderModeType } from "../../lib/ChoiceTableHelpers";
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { IDiagnosisCodeGroupItem } from '../../lib/Models/Setup/DiagnosisCodeGroupModel';
    import { DiagnosisCodeGroupXrefItem} from '../../lib/Api/DiagnosisCodeGroup';
    import { ICodeSearchItem, codeSearchDiagnosis } from '../../lib/ChoiceApi';
    import { popAlert, confirmDialog } from '../../lib/Notification';
    import { IToolbar, ToolbarStyle, ToolbarBackground, ToolbarPadding } from "../../lib/Toolbar";
    import { ModalSize } from "../../Enums/UiEnums";

    import TextInput from '../../components/TextInput.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import Combo from "../../components/Combo.vue";
    import Toolbar from "../../components/Toolbar.vue";
    import ModalDialog from '../../components/ModalDialog.vue'
    import MessageSection from '../../components/Messages/MessageSection.vue';

    const groupTableHeight = ref<number>();
    const searchTableHeight = ref<number>();
    const SearchTable = ref();
    const PerformSearchButton = ref<InstanceType<typeof ActionButtonV2>>();
    const DiagnosisCodeGroupCombo = ref<InstanceType<typeof Combo>>();
    
    const searchColumns = ref<ICol[]>([
        { Name: '', DataField:"selected", RenderMode: RenderModeType.Selection },
        { Name: 'Code', DataField: 'code' },
        { Name: 'Code Type', DataField: 'codeType' },
        { Name: 'Description', DataField: 'codeShortDesc' }
    ]);

    const groupColumns = ref<ICol[]>([
        { Name: '', DataField:"selected", RenderMode: RenderModeType.Selection },
        { Name: 'Code', DataField: 'code' },
        { Name: 'Code Type', DataField: 'codeType' },
        { Name: 'Description', DataField: 'codeDescription' }
    ]);

    enum myToolbar {
        New, 
        Edit,
        Delete,
        Refresh
    }

    const toolbarConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: myToolbar.New, label: "", tooltip:"Create a new Diagnosis Code Group.", action: ActionButtonStyle.Add, performAction: () => onNew()},
            {identifier: myToolbar.Edit, label: "", tooltip:"Edit the Diagnosis Code Group.", action: ActionButtonStyle.Edit, enabled: false, performAction: () => onEdit()},
            {identifier: myToolbar.Delete, label: "", tooltip:"Delete the selected items.", action: ActionButtonStyle.Remove, enabled: false, performAction: () => onDelete()},
            {identifier: myToolbar.Refresh, label: "", tooltip:"Refresh.", action: ActionButtonStyle.Refresh, enabled: false, performAction: () => refreshGroupXrefList()},
        ]
    });

    const props = defineProps<{
        title: string,
        codeGroupList: IDiagnosisCodeGroupItem[],
        diagnosisCodeGroupId?: number
    }>();

    const formData = reactive<{
        searchText: string,
        codeGroupList: IDiagnosisCodeGroupItem[],
        groupData: DiagnosisCodeGroupXrefItem[],
        searchData: ICodeSearchItem[],
        selectedSearchData: number[],
        selectedCodeGroupId?: number,
        selectedCodeGroupName?: string,
        selectedGroupData: number[],
        showEdit: boolean,
        editGroupId?: number,
        editGroupName?: string
    }>({
        searchText: '',
        codeGroupList: [],
        groupData: [],
        searchData: [],
        selectedSearchData: [],
        selectedGroupData: [],
        showEdit: false,
    });

    onMounted(async () => {
        formData.codeGroupList = props.codeGroupList;
        if ((props.diagnosisCodeGroupId ?? 0) != 0) {
            formData.selectedCodeGroupId = props.diagnosisCodeGroupId;
            await nextTick();
            DiagnosisCodeGroupCombo.value?.onComboChange();
            toolbarEnable();
        }
        windowResize();
    });

    const editType = computed<string>(() => formData.editGroupId ? "Edit" : "New");
    const isCodeGroupSelected = computed<boolean>(() => formData.selectedCodeGroupId ? true : false);

    function windowResize() {
        groupTableHeight.value = window.innerHeight - 110;
        setSearchTableHeight();
    }

    function setSearchTableHeight() {
        searchTableHeight.value = window.innerHeight - 110 - (formData.searchData.length >= 50 ? 65 : 0);
    }

    function toolbarEnable() {
        const enable = formData.selectedCodeGroupId ? true : false;
        toolbarConfig.toolbarList[myToolbar.Edit].enabled = enable;
        toolbarConfig.toolbarList[myToolbar.Delete].enabled = enable;
        toolbarConfig.toolbarList[myToolbar.Refresh].enabled = enable;
    }

    async function keySearch() {
        await PerformSearchButton.value?.actionButtonClick();
    }

    async function peformSearch() {
        if (formData.searchText.length === 0) {
            popAlert('Please enter search text.');
            return;
        }
        const inputs = {
            searchText: formData.searchText,
            displayAllResults: false
        }
        formData.searchData = await codeSearchDiagnosis.search(inputs);
        setSearchTableHeight();
        formData.selectedSearchData = [];
    }

    function onSelectSearch(checked: boolean|undefined, colData: ICol, row?: Record<string, any>) {
        if (row) {
            const rowData = <ICodeSearchItem> row;
            if (checked) {
                formData.selectedSearchData?.push(rowData.codeId ?? 0);
            } else {
                if (!formData.selectedSearchData)
                    return;
                const index = formData.selectedSearchData.findIndex(item => item === rowData.codeId);
                if (index > -1)
                    formData.selectedSearchData.splice(index, 1);
            }
        }
        else {
            if (checked) {
                formData.searchData.forEach( (element) => {
                    formData.selectedSearchData?.push(element.codeId ?? 0);
                }); 
            }
            else {
                formData.selectedSearchData = [];
            }
        }   
    }

    async function peformAssign() {
        if (formData.selectedSearchData.length === 0) {
            popAlert('Please select at least one diagnosis code to assign.');
            return;
        }
        const inputs = {
            diagnosisCodeGroupId: formData.selectedCodeGroupId ?? 0,
            codeList: formData.selectedSearchData
        }
        const response = await ChoiceApi.DiagnosisCodeGroup.addCodeGroupXref(inputs);
        if (!response) {
            popAlert('Error assigning diagnosis codes.');
            return;
        }
        if (!response.passed) {
            popAlert(response.message ?? 'Error assigning diagnosis codes.');
            return;
        }
        await refreshGroupXrefList();
    }

    function onSelectGroup(checked: boolean|undefined, colData: ICol, row?: Record<string, any>) {
        if (row) {
            const rowData = <ICodeSearchItem> row;
            if (checked) {
                formData.selectedGroupData?.push(rowData.codeId ?? 0);
            } else {
                if (!formData.selectedGroupData)
                    return;
                const index = formData.selectedGroupData.findIndex(item => item === rowData.codeId);
                if (index > -1)
                    formData.selectedGroupData.splice(index, 1);
            }
        }
        else {
            if (checked) {
                formData.searchData.forEach( (element) => {
                    formData.selectedGroupData?.push(element.codeId ?? 0);
                }); 
            }
            else {
                formData.selectedGroupData = [];
            }
        }   
    }

    async function performDelete() {
        if (formData.selectedGroupData.length === 0) {
            popAlert('Please select at least one diagnosis code to delete.');
            return;
        }
        const inputs = {
            diagnosisCodeGroupId: formData.selectedCodeGroupId ?? 0,
            codeList: formData.selectedGroupData
        }
        const response = await ChoiceApi.DiagnosisCodeGroup.removeCodeGroupXref(inputs);
        if (!response) {
            popAlert('Error deleting diagnosis codes.');
            return;
        }
        await refreshGroupXrefList();
    }

    async function loadDiagnosisCodeGroup(selectedItem : any) : Promise<void> {
        if (selectedItem == undefined) {
            formData.selectedCodeGroupId = undefined;
            formData.selectedCodeGroupName = undefined;
            formData.groupData = [];
            return;
        }
        formData.selectedCodeGroupName = selectedItem.description;
        formData.selectedCodeGroupId = selectedItem.diagnosisCodeGroupId;
        toolbarEnable();
        formData.groupData = await ChoiceApi.DiagnosisCodeGroup.getCodeGroupXrefList(selectedItem.diagnosisCodeGroupId) ?? [];
    }

    async function onNew() {
        formData.editGroupId = undefined;
        formData.editGroupName = undefined;
        formData.showEdit = true;
    }

    async function onEdit() {
        formData.editGroupId = formData.selectedCodeGroupId;
        formData.editGroupName = formData.selectedCodeGroupName;
        formData.showEdit = true;
    }

    async function onDelete() {
        const msg = `Are you sure you want to remove the Diagnosis Code Group: ${formData.selectedCodeGroupName} and all it's associated codes?`;
        const result = await confirmDialog(msg, { RejectText: "Cancel", ConfirmText: "Confirm" });                
        if (result) {
            const response = await ChoiceApi.DiagnosisCodeGroup.deleteCodeGroup(formData.selectedCodeGroupId ?? 0);
            if (!response) {
                popAlert('Error deleting diagnosis code group.');
                return;
            }
            if (!response.passed) {
                popAlert(response.message ?? 'Error deleting diagnosis code group.');
                return;
            }
            formData.selectedCodeGroupId = undefined;
            formData.selectedCodeGroupName = undefined;
            toolbarEnable();
            await refreshGroupList();
            await refreshGroupXrefList();
        }    
    }

    async function refreshGroupXrefList() {
        formData.groupData = await ChoiceApi.DiagnosisCodeGroup.getCodeGroupXrefList(formData.selectedCodeGroupId ?? 0) ?? [];
        formData.selectedGroupData = [];
    }

    async function onHideEdit() {
        formData.showEdit = false;
    }

    async function saveEdit() {
        if (formData.editGroupName === "") {
            popAlert('Please enter a group name.');
            return;
        }
        const inputs = {
            diagnosisCodeGroupId: formData.editGroupId,
            description: formData.editGroupName ?? ""
        }
        const result = await ChoiceApi.DiagnosisCodeGroup.saveCodeGroup(inputs);
        if (!result) {
            popAlert('Error saving diagnosis code group.');
            return;
        }
        if (!result.passed) {
            popAlert(result.message ?? 'Error saving diagnosis code group.');
            return;
        }
        await refreshGroupList();
        formData.selectedCodeGroupId = result.diagnosisCodeGroupId;
        toolbarEnable();
        await refreshGroupXrefList();
        onHideEdit();
    }   

    async function refreshGroupList() {
        formData.codeGroupList = await ChoiceApi.DiagnosisCodeGroup.getCodeGroupList() ?? [];
    }
</script>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="row">
        <div class="col">
            <span class="isalus-screen-header">
                {{ props.title }}
            </span>
        </div>
    </div>
    <div class="row mt-2">
        <div class="col-6">
            <div class="row">
                <div class="col-4 pe-0">
                    <Combo 
                        ref="DiagnosisCodeGroupCombo"
                        :optionList="formData.codeGroupList"
                        optionValue="diagnosisCodeGroupId"
                        :includeBlank="false"
                        v-model:selected="formData.selectedCodeGroupId" 
                        @onChange="loadDiagnosisCodeGroup" />
                </div>
                <div class="col pe-0 ps-2">
                    <Toolbar 
                        :configuration="toolbarConfig" 
                        :toolbarStyle="ToolbarStyle.outline" 
                        :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                        :toolbarBackground="ToolbarBackground.white"/>  
                </div>
                <div class="col-auto ps-0">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Delete"
                        text="Delete"
                        spinText="Deleting..."
                        tooltip="Delete selected codes from the group"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.TextPrimary"  
                        :performAction="performDelete"/>            
                </div>            
            </div>
        </div>  
        <div class="col-6">
            <div class="row">
                <div class="col-auto">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Add"
                        text="Assign"
                        spinText="Asigning..."
                        tooltip="Assign the selected codes to the group"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.TextPrimary"  
                        :disabled="!isCodeGroupSelected"
                        :performAction="peformAssign"/>            
                </div>
                <div class="col"></div>
                <div class="col-4 pe-0">
                    <TextInput 
                        v-model:textValue="formData.searchText" 
                        cssFramework="vanilla"  
                        placeholder="Search by code, description"
                        @keydown.enter="keySearch">
                    </TextInput>            
                </div>
                <div class="col-auto ps-1">
                    <ActionButtonV2 
                        ref="PerformSearchButton"
                        :action="ActionButtonStyle.Search"
                        text="Search"
                        spinText="Searching..."
                        tooltip="Search diagnosis codes"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                        :performAction="peformSearch"/>            
                </div>
            </div>
        </div>
    </div>
    <div class="row mt-2">
        <div class="col-6">
            <ChoiceTable 
                ref="GroupTable"
                :columns="groupColumns" 
                :data="formData.groupData" 
                :show-filters="false" 
                :allowSorting="false"
                fontSize="smaller"
                :height="groupTableHeight" 
                @selectChanged="onSelectGroup"
                :key-value-data-member="'codeId'"/>
        </div>
        <div class="col-6">
            <div class="row">
                <div class="col">
                    <ChoiceTable 
                        ref="SearchTable"
                        :columns="searchColumns" 
                        :data="formData.searchData" 
                        :show-filters="false" 
                        :allowSorting="false"
                        fontSize="smaller"
                        :height="searchTableHeight" 
                        @selectChanged="onSelectSearch"
                        :key-value-data-member="'codeId'"/>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col">
                    <MessageSection :displayStyle="DisplayStyle.Danger" v-if="formData.searchData.length >= 50">
                        * NOTE: Only the first 50 results are displayed.  Please refine search criteria.
                    </MessageSection>
                </div>
            </div>
        </div>
    </div>
    <ModalDialog :show="formData.showEdit" :size="ModalSize.Medium" @hide="onHideEdit">
        <template #title>Diagnosis Code Group - {{ editType }}</template>
        <div class="row">
            <div class="col-auto pe-0">Group Name</div>
            <div class="col">
                <TextInput
                    v-model:textValue="formData.editGroupName" 
                    cssFramework="vanilla">
                </TextInput>  
            </div>
        </div>
        <div class="row mt-2">
            <div class="col"></div>
            <div class="col-auto">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Save"
                    text="OK"
                    spinText="Saving..."
                    tooltip="Save the changes"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                    :performAction="saveEdit"
                    :endingPadding="2"/>
                <ActionButtonV2 
                    :action="ActionButtonStyle.Close"
                    text="Cancel"
                    spinText="Cancelling..."
                    tooltip="Cancel the changes"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                    :performAction="onHideEdit"/>            
            </div>           
        </div>
    </ModalDialog>
</template>

